import { Button, Tag } from "antd";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { loadAllUniversity, loadUniqueLocations, deleteUniversity } from "@/redux/rtk/features/CRM/university/universitySlice";
import CommonDelete from "@/components/CommonUi/CommonDelete";
import { loadAllMajor } from "@/redux/rtk/features/CRM/major/majorSlice";
import usePermissions from "@/utils/usePermissions";
import { PlusOutlined, YoutubeOutlined, FilePdfOutlined } from "@ant-design/icons";
import { AdvancedFilter } from "@/components/CommonUi/AdvancedFilter";
import useUniversityFilter from "./useUniversityFilter";
import TableComponent from "@/components/CommonUi/TableComponent";
import UniversityCalculator from "./UniversityCalculator";
import { CalculatorOutlined } from "@ant-design/icons";
import { getAttachmentUrl } from "@/utils/functions";

export default function UniversityList() {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [pageConfig, setPageConfig] = useState({
        page: 1,
        count: 10,
    });

    const { list, loading, totalCount } = useSelector((state) => state.university);
    const { permissions } = usePermissions();
    const filters = useUniversityFilter();

    const [filtersOpen, setFiltersOpen] = useState(false);
    const [isCalculatorVisible, setIsCalculatorVisible] = useState(false);
    const canCreate = permissions?.includes("create-university");
    const canView = permissions?.includes("readSingle-university");

    useEffect(() => {
        dispatch(loadAllUniversity(pageConfig));
    }, [dispatch, pageConfig]);

    useEffect(() => {
        if (filtersOpen) {
            dispatch(loadAllMajor());
            dispatch(loadUniqueLocations());
        }
    }, [dispatch, filtersOpen]);

    const columns = [
        {
            title: t("university.universityName") || "University Name",
            dataIndex: "universityName",
            key: "universityName",
            render: (text, record) => (
                <Link
                    to={canView ? `/admin/crm/university/${record.id}` : "#"}
                    className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 font-medium"
                >
                    {text}
                </Link>
            ),
            renderCsv: (text) => text,
        },
        {
            title: t("common.email") || "Email",
            dataIndex: "email",
            key: "email",
            render: (email) => (
                <a
                    href={`mailto:${email}`}
                    className="text-gray-600 dark:text-gray-400 hover:text-blue-600"
                >
                    {email}
                </a>
            ),
            renderCsv: (email) => email,
        },
        {
            title: t("university.website") || "Website",
            dataIndex: "website",
            key: "website",
            render: (website) =>
                website ? (
                    <a
                        href={`https://${website}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 dark:text-blue-400"
                    >
                        {website}
                    </a>
                ) : (
                    <span className="text-gray-400">-</span>
                ),
            renderCsv: (website) => website,
        },
        {
            title: t("university.youtube_link") || "YouTube",
            dataIndex: "youtubeLink",
            key: "youtubeLink",
            render: (youtubeLink, record) => {
                const link = youtubeLink || record.youtube_link;
                return link ? (
                    <a
                        href={link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-red-600 hover:text-red-800 dark:text-red-400"
                    >
                        <YoutubeOutlined className="text-xl" />
                    </a>
                ) : (
                    <span className="text-gray-400">-</span>
                );
            },
            renderCsv: (youtubeLink, record) => youtubeLink || record.youtube_link,
        },
        {
            title: t("university.location") || "Location",
            dataIndex: "location",
            key: "location",
            render: (location) => location || <span className="text-gray-400">-</span>,
            renderCsv: (location) => location,
        },
        {
            title: t("university.majors") || "Majors",
            dataIndex: "majors",
            key: "majors",
            render: (majors) => (
                <div className="flex flex-wrap gap-1">
                    {majors && majors.length > 0 ? (
                        majors.slice(0, 3).map((major) => (
                            <Tag key={major.id} color="blue">
                                {major.name}
                            </Tag>
                        ))
                    ) : (
                        <span className="text-gray-400">{t("university.noMajors")}</span>
                    )}
                    {majors && majors.length > 3 && (
                        <Tag color="default">+{majors.length - 3} {t("common.more") || "more"}</Tag>
                    )}
                </div>
            ),
            renderCsv: (majors) => majors?.map(m => m.name).join(", "),
        },
        {
            title: t("common.status") || "Status",
            dataIndex: "status",
            key: "status",
            render: (status) => (
                <Tag color={status === "true" ? "green" : "red"}>
                    {status === "true" ? t("common.active") || "Active" : t("common.inactive") || "Inactive"}
                </Tag>
            ),
            renderCsv: (status) => status === "true" ? "Active" : "Inactive",
        },
        {
            title: t("university.file") || "File",
            dataIndex: "file",
            key: "file",
            render: (file) =>
                file ? (
                    <a
                        href={getAttachmentUrl(file)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-red-600 hover:text-red-800 dark:text-red-400"
                    >
                        <FilePdfOutlined className="text-xl" />
                    </a>
                ) : (
                    <span className="text-gray-400">-</span>
                ),
            renderCsv: (file) => file,
        },
        {
            title: t("common.action") || "Action",
            key: "action",
            render: (record) => [
                {
                    label: (
                        <Link
                            to={`/admin/crm/university/${record.id}`}
                            className="flex items-center gap-2"
                        >
                            {t("common.view") || "View"}
                        </Link>
                    ),
                    key: "view",
                },
                {
                    label: (
                        <CommonDelete
                            id={record.id}
                            title={t("common.delete")}
                            permission={"delete-university"}
                            deleteThunk={deleteUniversity}
                            loadThunk={loadAllUniversity}
                            query={pageConfig}
                        />
                    ),
                    key: "delete",
                },
            ],
            csvOff: true,
        },
    ];

    return (
        <TableComponent
            list={list}
            columns={columns}
            loading={loading}
            total={totalCount}
            setPageConfig={setPageConfig}
            title={t("university.universities") || "Universities"}
            isSearch
            filters={filters}
            componentTitle={t("university.universities") || "Universities"}
            extraFilter={
                <AdvancedFilter
                    page="university"
                    filters={filters}
                    pageConfig={pageConfig}
                    setPageConfig={setPageConfig}
                    onOpen={() => setFiltersOpen(true)}
                />
            }
            extra={
                <div className="flex gap-2">
                    <Button
                        icon={<CalculatorOutlined />}
                        onClick={() => setIsCalculatorVisible(true)}
                    >
                        {t("university.calculator.calculate") || "Calculator"}
                    </Button>
                    {canCreate && (
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={() => navigate("/admin/crm/university/new")}
                        >
                            {t("university.addUniversity") || "Add University"}
                        </Button>
                    )}
                    <UniversityCalculator
                        visible={isCalculatorVisible}
                        onCancel={() => setIsCalculatorVisible(false)}
                    />
                </div>
            }
        />
    );
}
